import React, { useState, useEffect } from 'react';
import { DashboardData } from '../types';
import { apiService } from '../services/apiService';
import { Sidebar, Header } from './Layout';
import { Overview } from './Overview';
import { BetaManagement } from './BetaManagement';
import { TestFlightTesters } from './TestFlightTesters';
import { AppStoreStats } from './AppStore';
import { Email } from './Email';

interface DashboardProps {
  onLogout: () => void;
}

const Dashboard: React.FC<DashboardProps> = ({ onLogout }) => {
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [dashboardData, setDashboardData] = useState<DashboardData | null>(null);
  const [activeSection, setActiveSection] = useState('overview');

  useEffect(() => {
    loadData();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const loadData = async () => {
    try {
      setIsLoading(true);
      setError('');
      const data = await apiService.getDashboardData();
      setDashboardData(data);
    } catch (err) {
      console.error('Error loading dashboard data:', err);
      if (err instanceof Error && err.message === 'Unauthorized') {
        onLogout();
        return;
      }
      setError('Failed to load dashboard data');
    } finally {
      setIsLoading(false);
    }
  };

  const handleLogout = async () => {
    try {
      await apiService.logout();
    } catch (err) {
      console.error('Logout error:', err);
    }
    onLogout();
  };

  const handleSectionChange = (section: string) => {
    setActiveSection(section);
  };

  const getSectionTitle = (section: string) => {
    const titles = {
      'overview': { title: 'Overview', icon: '📊' },
      'beta-management': { title: 'Beta Management', icon: '👥' },
      'messages': { title: 'Messages', icon: '💬' },
      'testflight': { title: 'TestFlight', icon: '🚀' },
      'testflight-testers': { title: 'TestFlight Testers', icon: '👥' },
      'appstore': { title: 'App Store Statistics', icon: '🏪' },
      'version': { title: 'Version Control', icon: '📱' }
    };
    return titles[section as keyof typeof titles] || { title: 'Dashboard', icon: '📊' };
  };

  if (isLoading) {
    return (
      <div className="dashboard-container">
        <div className="loading-spinner">
          <div className="spinner"></div>
          <p>Loading dashboard...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="dashboard-container">
        <div className="error-message">
          <h2>Error</h2>
          <p>{error}</p>
          <button onClick={handleLogout} className="logout-button">
            Back to Login
          </button>
        </div>
      </div>
    );
  }

  if (!dashboardData) {
    return (
      <div className="dashboard-container">
        <div className="error-message">
          <h2>No Data</h2>
          <p>No dashboard data available</p>
          <button onClick={loadData} className="btn btn-primary">
            Retry
          </button>
        </div>
      </div>
    );
  }

  const sectionInfo = getSectionTitle(activeSection);

  return (
    <div className="dashboard-container">
      <Sidebar 
        activeSection={activeSection}
        onSectionChange={handleSectionChange}
        onLogout={handleLogout}
      />
      
      <div className="main-content">
        <Header 
          title={sectionInfo.title}
          icon={sectionInfo.icon}
          onRefresh={loadData}
        />
        
        <div className="breadcrumb">
          Home - <span>{sectionInfo.title}</span>
        </div>
        
        <div className="content-area">
          {activeSection === 'overview' && (
            <div className="content-section active">
              <Overview data={dashboardData} onRefresh={loadData} />
            </div>
          )}
          
          {activeSection === 'beta-management' && (
            <div className="content-section active">
              <BetaManagement signups={dashboardData.signups} onRefresh={loadData} />
            </div>
          )}
          
          {activeSection === 'messages' && (
            <div className="content-section active">
              <Email />
            </div>
          )}
          
          {activeSection === 'testflight' && (
            <div className="content-section active">
              <div className="card">
                <h3>TestFlight Management</h3>
                <p>TestFlight functionality coming soon...</p>
              </div>
            </div>
          )}
          
          {activeSection === 'testflight-testers' && (
            <div className="content-section active">
              <TestFlightTesters onRefresh={loadData} />
            </div>
          )}
          
          {activeSection === 'appstore' && (
            <div className="content-section active">
              <AppStoreStats />
            </div>
          )}
          
          {activeSection === 'version' && (
            <div className="content-section active">
              <div className="card">
                <h3>Version Control</h3>
                <p>Version control functionality coming soon...</p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
